import type {
  FilterContext,
  ShaderPass,
  UniformValue,
  VideoFilterDefinition,
  VideoFilterRuntime,
} from '../../core/definitions';
import { num, parseColor } from '../shaderWrap';

const NO_PASSES: readonly ShaderPass[] = Object.freeze([]);

/**
 * Loads one image asset as an ImageBitmap for use as a sampler uniform. A
 * stale load (the asset id changed or the filter was destroyed meanwhile) is
 * discarded.
 */
class AssetImage {
  bitmap: ImageBitmap | null = null;
  private assetId = '';
  private token = 0;

  constructor(
    private readonly ctx: FilterContext,
    private readonly premultiply: boolean,
    private readonly label: string,
  ) {}

  set(assetId: unknown): void {
    const id = typeof assetId === 'string' ? assetId : '';
    if (id === this.assetId) return;
    this.assetId = id;
    const token = ++this.token;
    this.release();
    if (!id) return;
    void this.load(id, token);
  }

  destroy(): void {
    this.token++;
    this.release();
  }

  private async load(id: string, token: number): Promise<void> {
    try {
      const blob = await this.ctx.assets.getBlob(id);
      if (token !== this.token) return;
      if (!blob) {
        this.ctx.logger.log('warn', 'render', `${this.label}: image asset "${id}" was not found`);
        return;
      }
      const bitmap = await createImageBitmap(blob, {
        premultiplyAlpha: this.premultiply ? 'premultiply' : 'none',
        colorSpaceConversion: 'none',
      });
      if (token !== this.token) {
        bitmap.close();
        return;
      }
      this.bitmap = bitmap;
      this.ctx.invalidate();
    } catch (error) {
      if (token === this.token) this.ctx.logger.log('error', 'render', `${this.label}: could not decode image asset "${id}"`, error);
    }
  }

  private release(): void {
    if (!this.bitmap) return;
    this.bitmap.close();
    this.bitmap = null;
    this.ctx.invalidate();
  }
}

type ImageMaskSettings = { image: string; mode: string; color: string; opacity: number };

const MASK_MODES = ['alpha', 'luma', 'multiply', 'add'];

const IMAGE_MASK_FRAGMENT = `
uniform sampler2D uMask;
uniform int uMode;
uniform vec3 uTint;
uniform float uOpacity;
// Rec. 709 luma weights (ITU-R BT.709).
const vec3 LUMA = vec3(0.2126, 0.7152, 0.0722);
void main() {
  vec4 src = texture(uSource, vUv);
  // The image is stretched over the picture; it is premultiplied like every other texture.
  vec4 mask = texture(uMask, vUv);
  vec3 tinted = mask.rgb * uTint;
  if (uMode == 0) {
    fragColor = src * mix(1.0, mask.a, uOpacity);
  } else if (uMode == 1) {
    fragColor = src * mix(1.0, dot(tinted, LUMA), uOpacity);
  } else if (uMode == 2) {
    // Multiply by the image where it is opaque, leave the picture alone where it is transparent.
    vec3 factor = mix(vec3(1.0), tinted + (1.0 - mask.a), uOpacity);
    fragColor = vec4(src.rgb * factor, src.a);
  } else {
    fragColor = vec4(min(src.rgb + tinted * uOpacity * src.a, vec3(src.a)), src.a);
  }
}
`;

export const imageMask: VideoFilterDefinition<ImageMaskSettings> = {
  type: 'video',
  kind: 'image-mask',
  displayName: 'Image Mask/Blend',
  defaults: () => ({ image: '', mode: 'alpha', color: '#ffffff', opacity: 1 }),
  properties: () => [
    { type: 'asset', key: 'image', label: 'Image', accept: 'image/*' },
    {
      type: 'select',
      key: 'mode',
      label: 'Type',
      options: [
        { value: 'alpha', label: 'Alpha mask (alpha channel)' },
        { value: 'luma', label: 'Alpha mask (luminance)' },
        { value: 'multiply', label: 'Blend (multiply)' },
        { value: 'add', label: 'Blend (add)' },
      ],
    },
    { type: 'color', key: 'color', label: 'Color' },
    { type: 'number', key: 'opacity', label: 'Opacity', min: 0, max: 1, step: 0.01, slider: true },
  ],
  create(ctx, initial): VideoFilterRuntime<ImageMaskSettings> {
    const image = new AssetImage(ctx, true, 'Image mask');
    const texture: { texture: ImageBitmap | null; version: number } = { texture: null, version: 0 };
    const uniforms: Record<string, UniformValue> = { uMask: texture, uMode: 0, uTint: [1, 1, 1], uOpacity: 1 };
    const passes: ShaderPass[] = [{ fragment: IMAGE_MASK_FRAGMENT, uniforms }];
    const apply = (settings: ImageMaskSettings): void => {
      image.set(settings.image);
      uniforms.uMode = Math.max(0, MASK_MODES.indexOf(settings.mode));
      uniforms.uTint = parseColor(settings.color).slice(0, 3);
      uniforms.uOpacity = Math.min(1, Math.max(0, num(settings.opacity, 1)));
    };
    apply(initial);
    return {
      // Until the image has decoded the picture passes through unchanged.
      passes: () => {
        if (!image.bitmap) return NO_PASSES;
        texture.texture = image.bitmap;
        return passes;
      },
      update: apply,
      destroy: () => image.destroy(),
    };
  },
};

type LutSettings = { image: string; amount: number };

const LUT_FRAGMENT = `
uniform sampler2D uLut;
uniform float uAmount;
// 512x512 atlas of a 64^3 cube: 8x8 tiles, blue picks the tile (row-major),
// red and green index inside it. The two nearest blue slices are blended, and
// lookups stay half a texel inside each tile so neighbours do not bleed.
vec3 lookup(vec3 c) {
  float blue = c.b * 63.0;
  float slice0 = floor(blue);
  float slice1 = min(slice0 + 1.0, 63.0);
  vec2 inTile = (c.rg * 63.0 + 0.5) / 512.0;
  vec2 tile0 = vec2(mod(slice0, 8.0), floor(slice0 / 8.0)) / 8.0;
  vec2 tile1 = vec2(mod(slice1, 8.0), floor(slice1 / 8.0)) / 8.0;
  return mix(texture(uLut, tile0 + inTile).rgb, texture(uLut, tile1 + inTile).rgb, blue - slice0);
}
void main() {
  vec4 src = texture(uSource, vUv);
  if (src.a <= 0.0) { fragColor = vec4(0.0); return; }
  vec3 c = clamp(src.rgb / src.a, 0.0, 1.0);
  fragColor = vec4(mix(c, lookup(c), uAmount) * src.a, src.a);
}
`;

export const lut: VideoFilterDefinition<LutSettings> = {
  type: 'video',
  kind: 'lut',
  displayName: 'Apply LUT',
  defaults: () => ({ image: '', amount: 1 }),
  properties: () => [
    {
      type: 'asset',
      key: 'image',
      label: 'LUT image',
      accept: 'image/png',
      description: '512x512 PNG holding a 64x64x64 cube as 8x8 tiles.',
    },
    { type: 'number', key: 'amount', label: 'Amount', min: 0, max: 1, step: 0.01, slider: true },
  ],
  create(ctx, initial): VideoFilterRuntime<LutSettings> {
    const image = new AssetImage(ctx, false, 'LUT');
    const texture: { texture: ImageBitmap | null; version: number } = { texture: null, version: 0 };
    const uniforms: Record<string, UniformValue> = { uLut: texture, uAmount: 1 };
    const passes: ShaderPass[] = [{ fragment: LUT_FRAGMENT, uniforms }];
    let unsupported: ImageBitmap | null = null;
    const apply = (settings: LutSettings): void => {
      image.set(settings.image);
      uniforms.uAmount = Math.min(1, Math.max(0, num(settings.amount, 1)));
    };
    apply(initial);
    return {
      passes: () => {
        const bitmap = image.bitmap;
        if (!bitmap) return NO_PASSES;
        if (bitmap.width !== 512 || bitmap.height !== 512) {
          if (unsupported !== bitmap) {
            unsupported = bitmap;
            ctx.logger.log('warn', 'render', `LUT image is ${bitmap.width}x${bitmap.height}; only the 512x512 (8x8 tiles of 64) layout is supported`);
          }
          return NO_PASSES;
        }
        texture.texture = bitmap;
        return passes;
      },
      update: apply,
      destroy: () => image.destroy(),
    };
  },
};
